import type { ComponentChildren } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { parseServerTimestamp } from '@/utils/time'
import { RoomPlayer } from '../player/RoomPlayer'
import { PAUSED_PROMPT_DELAY_MS, useHasPlayed } from '../player/usePlayer'
import type { Player } from '../player/usePlayer'
import type { RoomState } from '../types'
import type { SlateSpeaker } from './ViewerSlate'
import { PlayPrompt } from './ViewerStages'
import { ViewerWaitingBand } from './ViewerWaitingBand'
import { playsAgain, startsPaused } from './waitingVideo'
import type { WaitingVideo } from './waitingVideo'

interface ViewerWaitingStageProps {
    /** Its own player, not the page's: the heartbeat reports the session's video, never this one. */
    player: Player
    video: WaitingVideo
    room: Pick<RoomState, 'StartUtc' | 'TimeZone'>
    speakers: SlateSpeaker[]
    now: number
    accentColor: string | null
    title: string
    /** The slate without the video, once the video has played its last or cannot play here. */
    fallback: ComponentChildren
}

/** Whether the page is on screen: a browser holds a background tab's autoplay back until the tab is shown. */
function usePageVisible(): boolean {
    const [visible, setVisible] = useState(() => document.visibilityState === 'visible')
    useEffect(() => {
        const onChange = () => setVisible(document.visibilityState === 'visible')
        document.addEventListener('visibilitychange', onChange)
        return () => document.removeEventListener('visibilitychange', onChange)
    }, [])
    return visible
}

/**
 * A ready player that has never played, still paused on a page on screen once it knows the video's length: the
 * browser refused even a muted start (iOS Low Power Mode does). Nobody is asked to press play on an ad they never saw,
 * so the slate shows. A pause after it has played (a click on it, the phone locked) is the viewer's, and is offered
 * Play instead; a background tab's held start is judged only once the tab is shown.
 */
function useRefusedToStart(player: Player, started: boolean): boolean {
    const media = player.media.value
    const visible = usePageVisible()
    const stalled = visible && !started && media.status === 'ready' && media.paused && !media.ended && player.duration.value !== null
    const [refused, setRefused] = useState(false)
    useEffect(() => {
        if (!stalled) return
        const timer = setTimeout(() => setRefused(true), PAUSED_PROMPT_DELAY_MS)
        return () => clearTimeout(timer)
    }, [stalled])
    return refused
}

/**
 * The holding slate with the web's waiting video on it (ORB-1118): the video at the top of the stage, muted to start,
 * and the countdown and presenters in a band under it, or over its foot where the stage is short; the header above
 * already carries the logo and title. A viewer whose system asks for less motion is offered Play rather than a video
 * that starts by itself, unlike a browser's refused start, which shows the plain slate. When a play ends it starts
 * again only if it can finish before the start (`playsAgain`); then, or when it cannot play at all, the plain slate
 * takes over until the start.
 */
export function ViewerWaitingStage({ player, video, room, speakers, now, accentColor, title, fallback }: ViewerWaitingStageProps) {
    const media = player.media.value
    const startMs = parseServerTimestamp(room.StartUtc)
    // Read once, so the player's autoplay never flips under a video already playing.
    const [heldStill] = useState(() => startsPaused(window))
    const started = useHasPlayed(player)
    const refused = useRefusedToStart(player, started || heldStill)
    const [done, setDone] = useState(false)
    const nowAtEnd = useRef(now)
    nowAtEnd.current = now

    useEffect(() => {
        if (!media.ended) return
        if (playsAgain(video.playsOnce, player.duration.value, startMs, nowAtEnd.current)) {
            player.seek(0)
            player.play()
        } else {
            setDone(true)
        }
    }, [media.ended])

    // Latched: the fallback unmounts the player, which would otherwise read as idle and mount it again.
    useEffect(() => {
        if (refused || media.status === 'failed') setDone(true)
    }, [refused, media.status])

    if (done) return <>{fallback}</>

    return (
        <div class="v-wait">
            <div class="v-wait-video">
                <RoomPlayer
                    player={player}
                    source={{ kind: 'on-demand', playbackId: video.playbackId, playbackToken: video.token, autoplay: !heldStill, muted: true, accentColor, title }}
                    failureClass="v-fail"
                />
                {(started || heldStill) && <PlayPrompt player={player} />}
            </div>
            <ViewerWaitingBand room={room} speakers={speakers} now={now} />
        </div>
    )
}
